/**
 * Shapes for the Chart of Accounts screen — the account tree and its derived,
 * read-only balances.
 *
 * These now mirror the **live** bos-api ledger accounts contract (BOS-91). Two
 * facts about that contract shape everything below:
 *
 * 1. **The account carries its own balance.** There is no separate
 *    trial-balance endpoint any more; `GET /accounts` returns the balance on the
 *    row. Anything that used to read a trial balance reads the list.
 * 2. **`archived` is the live/not-live bit, and it is true when the account is
 *    NOT live.** There is no `active` field anywhere in this client, deliberately:
 *    the same physical bit under the opposite label is the single easiest thing
 *    to invert by accident, and an inversion silently shows archived accounts as
 *    live. If you find yourself writing `active`, you are about to introduce that
 *    bug — use `archived` and read it as "hidden".
 *
 * IMPORTANT: all client-side money arithmetic is done in integer **tetri**
 * (1 GEL = 100 tetri). The decimal strings are for display only and must never
 * be summed. See `formatGel`.
 */

/** Locales the app renders account names in (mirrors i18n `supportedLanguages`). */
export type LocaleCode = 'ka' | 'en';

export type AccountType =
    'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';

/** The side an account's balance normally sits on. */
export type NormalBalance = 'DEBIT' | 'CREDIT';

/**
 * VAT treatment tag carried by the account. The concrete enum lives on the
 * backend; the screen only reads it (never presents it), so it is typed loosely
 * here and can be narrowed once the ledger API documents the closed set.
 *
 * The set *is* now documented — `NONE | INPUT | OUTPUT`, modelled as
 * {@link VatRole} in `models/Vat` — and this tag is what the VAT report
 * classifies by. Narrowing it is an accounts change, not a VAT one: the chart
 * screens read this field from every account list row, so it needs its own pass
 * over the mapping and the account form.
 */
export type AccountVatRole = string | null;

/**
 * Tags the account for a ledger flow that has to find one specific account
 * without knowing the chart's numbering — the same idea as {@link AccountVatRole},
 * for the flows outside VAT.
 *
 * Each of these was a hardcoded account code on the server until BOS-107. When
 * the chart was replaced with the Georgian standard plan the codes silently
 * stopped meaning what the server thought they meant, and opening balances,
 * year-end close and asset disposal each failed the moment they were used. A tag
 * survives a renumbering; a code does not.
 *
 * **At most one postable account may hold any role other than `NONE`.** The
 * server resolves a role by taking the single match and treats two as a
 * configuration error rather than picking one — so a second holder does not fail
 * at save time, it fails later, inside a flow that has nothing to do with the
 * edit that caused it. The form checks the chart before offering a role for
 * exactly that reason.
 *
 * `null` and `'NONE'` both mean "plays no special role"; the mapping normalises
 * an absent tag to `null`.
 */
/**
 * Every role, in the order a period uses them: open the books, carry last year's
 * result, park this year's, then the two disposal outcomes.
 *
 * The runtime list is the single source of truth and {@link AccountRole} is
 * derived from it, so the wire guard, the form's option list and the type cannot
 * drift apart. Adding a role here is a compile error everywhere it must be
 * handled.
 */
export const ACCOUNT_ROLES = [
    'NONE',
    'OPENING_BALANCE_EQUITY',
    'RETAINED_EARNINGS',
    'CURRENT_YEAR_RESULT',
    'DISPOSAL_GAIN',
    'DISPOSAL_LOSS',
] as const;

export type AccountRole = (typeof ACCOUNT_ROLES)[number];

/** The tag as carried on an account: a known role, or `null` when untagged. */
export type AccountRoleTag = AccountRole | null;

/**
 * Where the account lands on a financial statement, and which cash-flow section
 * it belongs to. Both are optional create-time fields on the live contract and
 * are otherwise opaque to this client — it stores and echoes them, it never
 * interprets them.
 *
 * OPEN ITEM (reported): whether statements map correctly for an account created
 * without them. If they are in fact required, the create form is incomplete and
 * these stop being optional.
 */
export type AccountStatementMapping = string | null;
export type AccountCashFlowClass = string | null;

/**
 * A derived balance for one account, per the money-serialization contract.
 *
 * - `naturalBalance*` — the balance in the account's own normal direction
 *   (a normal position is positive).
 * - `signedBalance*` — signed to a fixed **debit-positive** convention, so
 *   sibling and descendant balances can be summed directly regardless of type.
 *
 * The `*Tetri` fields are integers (tetri) and are the only values used for
 * arithmetic; the decimal strings are display echoes. The live contract ships
 * `signedBalanceTetri` as authoritative; `natural*` is derived from it and the
 * account's `normalBalance` when the wire does not carry it (see
 * `accountsMapping`).
 */
export type AccountBalance = {
    accountId: string;
    naturalBalance: string;
    naturalBalanceTetri: number;
    signedBalance: string;
    signedBalanceTetri: number;
};

/** Fields shared by a list row and the by-id detail. */
type AccountCommon = {
    id: string;
    code: string;
    type: AccountType;
    parentId: string | null;
    normalBalance: NormalBalance;
    isContra: boolean;
    isPostable: boolean;
    vatRole: AccountVatRole;
    /**
     * Ledger-flow tag, or `null` when the account plays no special role. See
     * {@link AccountRole} — notably, only one account may hold each non-`NONE`
     * role, and the server does not stop a second one being saved.
     */
    accountRole: AccountRoleTag;
    /**
     * `true` = archived (hidden from the live chart). `false` = live.
     * See the header note — there is no `active` anywhere.
     */
    archived: boolean;
    /**
     * Whether the account has lines in a **POSTED or REVERSED** entry whose
     * source is not `OPENING`. Draft entries and the opening-balance seed entry
     * deliberately don't count, so a mis-classified seed account stays
     * correctable until real activity lands.
     *
     * Once true the structural fields (`type`, `normalBalance`, `isContra`) are
     * frozen — the server rejects a change with
     * `STRUCTURAL_FIELD_LOCKED_POSTED`; the form disables them so the attempt is
     * never made. Required, not optional: the live contract always carries it.
     *
     * Today it is `false` for every account because postings are not integrated
     * yet, so the lock reads as inert. That is correct, not a regression — it
     * goes live when postings arrive, with no contract change here.
     */
    hasPostings: boolean;
    /**
     * Opaque optimistic-concurrency token. Echo the one you last saw on the next
     * write; a mismatch comes back as `409 STALE_UPDATE`. Required — every read
     * carries one, and a write without it cannot be made safely.
     */
    etag: string;
    statementMapping?: AccountStatementMapping;
    cashFlowClass?: AccountCashFlowClass;
};

/**
 * One row of `GET /accounts?locale=…`.
 *
 * `name` is the **server-resolved display string** for the requested locale, not
 * a locale map — the list ships one name. It is typed as a plain string on
 * purpose so nothing downstream can quietly index a locale that isn't there;
 * the edit drawer fetches {@link AccountDetail} when it needs both languages.
 *
 * Consequence worth knowing: search over the chart therefore spans the resolved
 * name only, not every localization. Searching "Cash" in Georgian matches the
 * Georgian names the server sent, which is the same set the user can see.
 */
export type AccountListItem = AccountCommon & {
    name: string;
    /** `null` when the account has no balance (never posted to, never seeded). */
    balance: AccountBalance | null;
};

/**
 * `GET /accounts/{id}` — the full record, with every locale of the name. The
 * edit drawer needs this: it has separate Georgian and English fields and a list
 * row cannot populate both.
 */
export type AccountDetail = AccountCommon & {
    name: Partial<Record<LocaleCode, string>>;
    balance: AccountBalance | null;
};

/**
 * `GET /accounts` — the full chart (unpaginated; ~60 rows).
 *
 * `bootstrapRequired` distinguishes two empty lists that mean opposite things
 * and must never be conflated:
 * - `items: []` + `bootstrapRequired: true` — a fresh tenant with no chart at
 *   all. Route to onboarding.
 * - `items: []` + `bootstrapRequired: false` — everything was filtered out (all
 *   archived, with `includeArchived=false`). An ordinary empty result.
 */
export type GetAccountListResponse = {
    items: AccountListItem[];
    bootstrapRequired: boolean;
};

/**
 * The screen's working row type. Historically this was "an account joined to a
 * balance from the trial-balance feed"; the balance now arrives on the row, so
 * it is simply the list item. The alias is kept because the tree builder, the
 * grid, and every subsidiary-ledger tie-out are written against the name.
 */
export type ChartAccount = AccountListItem;
